import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="max-w-md mx-auto px-6 py-24 lg:py-32">
      <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-4">◦ Account recovery</div>
      <h1 className="font-serif text-5xl lg:text-6xl tracking-tight font-light">Reset.</h1>

      {sent ? (
        <div data-testid="forgot-password-sent" className="mt-12 border border-border rounded-sm p-8 bg-card">
          <MailCheck className="w-7 h-7 text-primary" />
          <div className="font-serif text-2xl mt-4">Check your inbox</div>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            If an account exists for <span className="text-foreground">{email}</span>, we've sent a link to reset your password.
            It expires in 1 hour. Be sure to check your spam folder.
          </p>
          <Link to="/login" className="inline-block mt-6 text-sm text-foreground hover:text-primary transition-colors">← Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-12 space-y-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Enter the email tied to your account and we'll send you a secure link to set a new password.
          </p>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Email</label>
            <input
              data-testid="forgot-email-input"
              type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 text-base transition-colors"
            />
          </div>
          {error && <div data-testid="forgot-error" className="text-sm text-destructive">{error}</div>}
          <button
            data-testid="forgot-submit"
            disabled={loading}
            className="btn-primary w-full py-3.5 text-sm tracking-wide rounded-sm disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send reset link"}
          </button>
          <div className="text-sm text-muted-foreground text-center pt-4">
            Remembered it? <Link to="/login" className="text-foreground hover:text-primary transition-colors">Back to sign in →</Link>
          </div>
        </form>
      )}
    </div>
  );
}
